import type { FormInstance } from 'antd/lib/form';
import React from 'react';

const strongPasswordRegex = {
  lowercase: /[a-z]/,
  uppercase: /[A-Z]/,
  number: /\d/,
  special: /[!@#%^$&*.\-]/,
};

const allowedPasswordChars = /^[a-zA-Z0-9!@#%^$&*.\-]*$/;

type IsRepeatedPasswordValidator = (params: { form: FormInstance }) => Promise<void>;
type IsPasswordReusedValidator = (params: { form: FormInstance }) => Promise<void>;
type IsPasswordValidator = (params: { form: FormInstance }) => Promise<void>;
type IsPasswordSameValidator = (params: { form: FormInstance }) => Promise<void>;
type HandleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => void;
type HandlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => void;
type HandleValuePassword = (e: React.ChangeEvent<HTMLInputElement>) => void;

type UsePasswordValidate = () => {
  isRepeatedPasswordValidator: IsRepeatedPasswordValidator;
  isPasswordReusedValidator: IsPasswordReusedValidator;
  isPasswordValidator: IsPasswordValidator;
  isPasswordSameValidator: IsPasswordSameValidator;
  handleKeyPress: HandleKeyPress;
  handlePaste: HandlePaste;
  handleValuePassword: HandleValuePassword;
};

// Проверка что пароль подтверждения соответствует новому паролю
const isRepeatedPasswordValidator: IsRepeatedPasswordValidator = ({ form }) => {
  const newPassword = form.getFieldValue('newPassword');
  const repeatedPassword = form.getFieldValue('repeatedPassword');
  if (repeatedPassword && repeatedPassword !== newPassword) {
    return Promise.reject();
  }
  return Promise.resolve();
};

// Проверка что пароль не меняется на старый
const isPasswordReusedValidator: IsPasswordReusedValidator = ({ form }) => {
  const newPassword = form.getFieldValue('newPassword');
  const oldPassword = form.getFieldValue('oldPassword');
  if (newPassword === oldPassword) {
    return Promise.reject();
  }
  return Promise.resolve();
};

// Проверка что пароль соответствует требованиям
const isPasswordValidator: IsPasswordValidator = ({ form }) => {
  const newPassword = form.getFieldValue('newPassword');
  const checks = {
    length: newPassword?.length >= 8,
    lowercase: strongPasswordRegex.lowercase.test(newPassword),
    uppercase: strongPasswordRegex.uppercase.test(newPassword),
    number: strongPasswordRegex.number.test(newPassword),
    special: strongPasswordRegex.special.test(newPassword),
  };

  const passedGroups = Object.values(checks)?.filter(Boolean)?.length;
  const isValid = checks?.length && passedGroups >= 5;

  if (!isValid) {
    return Promise.reject();
  }
  return Promise.resolve();
};

// Проверка что пароль присутствует
const isPasswordSameValidator: IsPasswordSameValidator = ({ form }) => {
  const newPassword = form.getFieldValue('newPassword');

  if (!newPassword || newPassword?.length === 0) {
    return Promise.reject();
  }
  return Promise.resolve();
};

// Обработчики для валидации ввода
const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (!/[a-zA-Z0-9!@#%^$&*.\-]/.test(e.key)) {
    e.preventDefault();
  }
};

// Обработчик для экрана восстановления пароля
const handleValuePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  return value.replace(/[^a-zA-Z0-9\-!@#%^$&*.]/g, '');
};

// Обработчики для валидации вставки
const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
  const pastedData = e.clipboardData.getData('Text');
  if (!allowedPasswordChars.test(pastedData)) {
    e.preventDefault();
  }
};

const usePasswordValidate: UsePasswordValidate = () => ({
  isRepeatedPasswordValidator,
  isPasswordReusedValidator,
  isPasswordValidator,
  isPasswordSameValidator,
  handleKeyPress,
  handlePaste,
  handleValuePassword,
});

export default usePasswordValidate;
